import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const fetchMessages = createAsyncThunk(
  'message/fetchMessages',
  async (params, { rejectWithValue }) => {
    try {
      // 转换前端分页参数到后端分页参数
      const apiParams = {
        ...params,
        page: params.current || params.page || 1,
        limit: params.pageSize || params.limit || 10,
      };
      // 删除前端特有的字段
      delete apiParams.current;
      delete apiParams.pageSize;
      
      const response = await api.get('/admin/messages', { params: apiParams });
      return response.data.data || response.data; // 兼容包裹在data中的响应
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取消息列表失败');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'message/sendMessage',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/messages/send', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '发送消息失败');
    }
  }
);

export const batchSendMessage = createAsyncThunk(
  'message/batchSendMessage',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/messages/batch-send', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '批量发送消息失败');
    }
  }
);

export const markMessageAsRead = createAsyncThunk(
  'message/markMessageAsRead',
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/messages/${messageId}/read`);
      return { ...response.data, messageId }; // 确保返回messageId
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '标记消息已读失败');
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'message/deleteMessage',
  async (messageId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/messages/${messageId}`);
      return { data: response.data, messageId }; // 返回数据和消息ID
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '删除消息失败');
    }
  }
);

// 发送消息给所有用户
export const sendToAll = createAsyncThunk(
  'message/sendToAll',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/messages/send-to-all', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '发送消息给所有用户失败');
    }
  }
);

// 按等级发送消息
export const sendByLevels = createAsyncThunk(
  'message/sendByLevels',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/messages/send-by-levels', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '按等级发送消息失败');
    }
  }
);

// 搜索用户（用于用户选择器）
export const searchUsers = createAsyncThunk(
  'message/searchUsers',
  async (params, { rejectWithValue }) => {
    try {
      const { keyword, page = 1, limit = 20 } = params || {};
      const response = await api.get('/admin/users/search', {
        params: { keyword, page, limit }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '搜索用户失败');
    }
  }
);

// 初始状态
const initialState = {
  messages: [],
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {},
  isLoading: false,
  error: null,
};

// slice
const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取消息列表
      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          // 支持多种数据格式：messages, list
          state.messages = action.payload.messages || action.payload.list || [];
          // 处理分页数据
          if (action.payload.pagination) {
            state.pagination = {
              ...state.pagination,
              current: action.payload.pagination.current || action.payload.pagination.page || state.pagination.current,
              pageSize: action.payload.pagination.pageSize || action.payload.pagination.limit || state.pagination.pageSize,
              total: action.payload.pagination.total || 0,
              pages: action.payload.pagination.pages || 0,
            };
          }
        }
        state.error = null;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 发送消息
      .addCase(sendMessage.fulfilled, (state, action) => {
        if (action.payload) {
          state.messages.unshift(action.payload);
        }
      })
      // 批量发送消息
      .addCase(batchSendMessage.fulfilled, (state, action) => {
        // 批量发送成功后刷新消息列表
      })
      // 标记消息已读
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        if (action.payload) {
          // 支持多种ID字段格式
          const messageId = action.payload.id || action.payload.message_id || action.payload.messageId;
          if (messageId) {
            const index = state.messages.findIndex(message => 
              (message.id || message.message_id || message.messageId) === messageId
            );
            if (index !== -1) {
              state.messages[index] = {
                ...state.messages[index],
                ...action.payload,
                status: 'read',
              };
            }
          }
        }
      })
      // 删除消息
      .addCase(deleteMessage.fulfilled, (state, action) => {
        if (action.payload && action.payload.messageId) {
          state.messages = state.messages.filter(message => {
            const id = message.id || message.message_id || message.messageId;
            return id !== action.payload.messageId;
          });
          // 更新总数
          if (state.pagination.total > 0) {
            state.pagination.total -= 1;
          }
        }
      })
      // 发送消息给所有用户
      .addCase(sendToAll.fulfilled, (state, action) => {
        // 发送成功后可以刷新消息列表
      })
      // 按等级发送消息
      .addCase(sendByLevels.fulfilled, (state, action) => {
        // 发送成功后可以刷新消息列表
      })
      // 搜索用户
      .addCase(searchUsers.fulfilled, (state, action) => {
        // 用户搜索结果不需要存储在 state 中，直接返回给组件使用
      });
  },
});

export const { clearError, setFilters, setPagination } = messageSlice.actions;
export default messageSlice.reducer;
